import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { TypeIcon, useCheckOff } from '../components/CheckOff';
import { ACTIVITY, ACTIVITY_TYPES, type ActivityType } from '../lib/activity';
import { Link, useNavigate, useParams } from 'react-router';
import { DealEconomics } from '../components/DealEconomics';
import { PAYMENT_COLOR, StagePill } from '../components/DealBits';
import { BuildingPicker, ContactPicker, OrgPicker } from '../components/Pickers';
import { RemindersPanel } from '../components/RemindersPanel';
import { Timeline } from '../components/Timeline';
import { Button, Card, cx, Empty, ErrorBox, Field, FieldGroup, Input, Modal, PageHeader, Pill, Select, Spinner, Stat, Textarea } from '../components/ui';
import { syncDeal } from '../lib/dealEngine';
import { DEAL_TYPE_LABEL, isLeaseDeal } from '../lib/deals';
import { date, money, personName, todayISO } from '../lib/format';
import { supabase, unwrap, unwrapMaybe } from '../lib/supabase';

function useDeal(id: string) {
  return useQuery({
    queryKey: ['deal', id],
    queryFn: async () => {
      const [deal, terms, abatements, fees, payout, payments, contacts, interactions] = await Promise.all([
        supabase
          .from('deals')
          .select('*, contacts!deals_primary_contact_id_fkey(id, first_name, last_name, display_name), organizations(id, name), buildings(id, name, street, city), suites(suite_number)')
          .eq('id', id)
          .single(),
        supabase.from('deal_lease_terms').select('*').eq('deal_id', id).maybeSingle(),
        supabase.from('deal_rent_abatements').select('*').eq('deal_id', id).order('start_month'),
        supabase.from('fee_components').select('*').eq('deal_id', id).order('sort_order'),
        supabase.from('payout_structures').select('*').eq('deal_id', id).maybeSingle(),
        supabase.from('payments_effective').select('*').eq('deal_id', id).order('expected_date', { nullsFirst: false }),
        supabase.from('deal_contacts').select('id, role, contacts(id, first_name, last_name, display_name)').eq('deal_id', id),
        supabase
          .from('interactions')
          .select('id, interaction_type, direction, occurred_at, subject, body_html, body_text, snippet, duration_seconds, source, source_link, thread_id, contact_id, participants, has_attachments, body_storage, mail_account_id, external_id, outcome, reminder_id')
          .eq('deal_id', id)
          .order('occurred_at', { ascending: false }),
      ]);
      const d = unwrap(deal);
      const stages = unwrap(await supabase.from('deal_stages').select('stage_key, label, probability, is_closed').eq('deal_type', d.deal_type).order('sort_order'));
      return {
        deal: d,
        stages,
        terms: unwrapMaybe(terms),
        abatements: unwrap(abatements),
        fees: unwrap(fees),
        payout: unwrapMaybe(payout),
        payments: unwrap(payments),
        contacts: unwrap(contacts),
        interactions: unwrap(interactions),
      };
    },
  });
}

export function DealDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, error } = useDeal(id);
  const [editing, setEditing] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [editingStep, setEditingStep] = useState(false);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['deal', id] });
    qc.invalidateQueries({ queryKey: ['pipeline'] });
    qc.invalidateQueries({ queryKey: ['revenue'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };
  const { checkOffStep } = useCheckOff();

  const setStage = useMutation({
    mutationFn: async (stage: string) => {
      unwrap(await supabase.from('deals').update({ stage }).eq('id', id));
      await syncDeal(id);
    },
    onSuccess: refresh,
  });
  const del = useMutation({
    mutationFn: async () => unwrap(await supabase.from('deals').delete().eq('id', id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipeline'] });
      navigate('/pipeline');
    },
  });
  const addContact = useMutation({
    mutationFn: async ({ contactId, role }: { contactId: string; role: string }) => unwrap(await supabase.from('deal_contacts').insert({ deal_id: id, contact_id: contactId, role })),
    onSuccess: () => {
      setAddingContact(false);
      refresh();
    },
  });
  const removeContact = useMutation({
    mutationFn: async (linkId: string) => unwrap(await supabase.from('deal_contacts').delete().eq('id', linkId)),
    onSuccess: refresh,
  });

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorBox error={error ?? 'Deal not found'} />;
  const { deal, payments } = data;
  const isLease = isLeaseDeal(deal.deal_type);
  const stage = data.stages.find((s) => s.stage_key === deal.stage);
  const prob = Number(stage?.probability ?? 0);
  const total = payments.reduce((t, p) => t + Number(p.amount ?? 0), 0);
  const received = payments.filter((p) => p.status === 'received').reduce((t, p) => t + Number(p.received_amount ?? p.amount ?? 0), 0);
  const weighted = received + payments.filter((p) => p.status !== 'received').reduce((t, p) => t + Number(p.amount ?? 0) * prob, 0);
  const next = payments.find((p) => p.status !== 'received' && p.expected_date);
  const unpaid = payments.filter((p) => p.status !== 'received').length;
  const primary = deal.contacts;

  return (
    <div>
      <PageHeader
        title={deal.name}
        subtitle={
          <div className="flex flex-wrap items-center gap-2">
            <Pill>{DEAL_TYPE_LABEL[deal.deal_type]}</Pill>
            {primary && (
              <Link to={`/contacts/${primary.id}`} className="hover:text-indigo-700 hover:underline">
                {personName(primary)}
              </Link>
            )}
            {deal.organizations && (
              <Link to={`/companies/${deal.organizations.id}`} className="hover:text-indigo-700 hover:underline">
                · {deal.organizations.name}
              </Link>
            )}
            {deal.buildings && (
              <Link to={`/buildings/${deal.buildings.id}`} className="hover:text-indigo-700 hover:underline">
                · {deal.buildings.name || deal.buildings.street}
                {deal.suites?.suite_number && ` ${deal.suites.suite_number}`}
              </Link>
            )}
          </div>
        }
        actions={
          <>
            <Select
              value={deal.stage}
              onChange={(e) => {
                const target = data.stages.find((s) => s.stage_key === e.target.value);
                if (target?.is_closed && unpaid > 0) {
                  alert(`This deal can't move to "${target.label}" yet: ${unpaid} payment(s) aren't marked received. Mark them received first.`);
                  return;
                }
                setStage.mutate(e.target.value);
              }}
              className="!w-52"
            >
              {data.stages.map((s) => (
                <option key={s.stage_key} value={s.stage_key}>
                  {s.label} ({Math.round(Number(s.probability) * 100)}%)
                </option>
              ))}
            </Select>
            <Button onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" /> Edit
            </Button>
          </>
        }
      />
      <ErrorBox error={setStage.error} />
      {!primary && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span>
            <b>No primary contact.</b> The previous contact was deleted — assign who this deal is with now.
          </span>
          <Button size="sm" onClick={() => setEditing(true)}>
            Assign contact
          </Button>
        </div>
      )}

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Commission" value={money(total, false)} sub={<StagePill stage={deal.stage} label={`${stage?.label} · ${Math.round(prob * 100)}%`} />} />
        <Stat label="Weighted" value={money(weighted, false)} />
        <Stat label="Received" value={money(received, false)} />
        <Stat label="Next payment" value={next ? date(next.expected_date) : '—'} sub={next ? money(next.amount) : undefined} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <PaymentsCard dealId={id} payments={payments} onChange={refresh} />
          <DealEconomics
            key={deal.updated_at}
            deal={deal}
            isLease={isLease}
            terms={data.terms}
            abatements={data.abatements}
            fees={data.fees}
            payout={data.payout}
          />
        </div>
        <div className="space-y-4">
          <Card
            title="Next step"
            actions={
              <Button size="sm" variant="ghost" onClick={() => setEditingStep(true)}>
                {deal.next_step ? 'Edit' : '+ Add'}
              </Button>
            }
          >
            {deal.next_step ? (
              <div className="flex items-start gap-2.5 text-sm text-slate-800">
                <input
                  type="checkbox"
                  className="mt-1"
                  title="Mark done"
                  aria-label="Mark done"
                  onChange={() => checkOffStep({ dealId: id, type: deal.next_step_type, name: deal.contacts ? personName(deal.contacts) : deal.name })}
                />
                <TypeIcon type={deal.next_step_type} />
                <div>
                  {deal.next_step}
                  {deal.next_step_date && <div className={`mt-1 text-xs ${deal.next_step_date < todayISO() ? 'font-medium text-rose-600' : 'text-slate-500'}`}>by {date(deal.next_step_date)}</div>}
                </div>
              </div>
            ) : (
              <Empty>No next step set.</Empty>
            )}
            {deal.expected_close_date && <div className="mt-2 text-xs text-slate-500">Expected close {date(deal.expected_close_date)}</div>}
            {deal.notes && <p className="mt-2 whitespace-pre-wrap border-t border-slate-100 pt-2 text-sm text-slate-600">{deal.notes}</p>}
          </Card>
          <Card title="People on this deal" actions={<Button size="sm" variant="ghost" onClick={() => setAddingContact(true)}>+ Add</Button>}>
            <ul className="space-y-1.5 text-sm">
              {primary && (
                <li>
                  <Link to={`/contacts/${primary.id}`} className="font-medium hover:text-indigo-700">
                    {personName(primary)}
                  </Link>{' '}
                  <span className="text-xs text-slate-500">primary</span>
                </li>
              )}
              {data.contacts.map((c) =>
                c.contacts ? (
                  <li key={c.id} className="group flex items-center gap-2">
                    <Link to={`/contacts/${c.contacts.id}`} className="hover:text-indigo-700">
                      {personName(c.contacts)}
                    </Link>
                    <span className="text-xs text-slate-500">{c.role}</span>
                    <button className="ml-auto hidden text-slate-300 hover:text-rose-600 group-hover:block" onClick={() => removeContact.mutate(c.id)} aria-label="Remove">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ) : null,
              )}
            </ul>
          </Card>
          <Card title="Reminders">
            <RemindersPanel dealId={id} label={deal.name} />
          </Card>
          {data.interactions.length > 0 && (
            <Card title="Activity on this deal">
              <Timeline items={data.interactions} />
            </Card>
          )}
          <button className="text-xs text-slate-400 hover:text-rose-600 hover:underline" onClick={() => confirm(`Delete "${deal.name}" and its payments? This can't be undone.`) && del.mutate()}>
            <Trash2 className="inline h-3 w-3" /> delete deal
          </button>
          <ErrorBox error={del.error} />
        </div>
      </div>

      <Modal title="Edit deal" open={editing} onClose={() => setEditing(false)}>
        <DealInfoForm
          deal={deal}
          onDone={() => {
            setEditing(false);
            refresh();
          }}
        />
      </Modal>
      <Modal title="Next step" open={editingStep} onClose={() => setEditingStep(false)}>
        <NextStepForm
          dealId={id}
          step={deal.next_step ?? ''}
          due={deal.next_step_date ?? ''}
          type={(deal.next_step_type as ActivityType | null) ?? null}
          onDone={() => {
            setEditingStep(false);
            refresh();
          }}
        />
      </Modal>
      <Modal title="Add a person to this deal" open={addingContact} onClose={() => setAddingContact(false)}>
        <AddDealContact onAdd={(contactId, role) => addContact.mutate({ contactId, role })} />
        <ErrorBox error={addContact.error} />
      </Modal>
    </div>
  );
}

function NextStepForm({ dealId, step, due, type, onDone }: { dealId: string; step: string; due: string; type: ActivityType | null; onDone: () => void }) {
  const [text, setText] = useState(step);
  const [by, setBy] = useState(due);
  const [kind, setKind] = useState<ActivityType | null>(type);
  const save = useMutation({
    mutationFn: async () =>
      unwrap(await supabase.from('deals').update({ next_step: text.trim() || null, next_step_date: by || null, next_step_type: text.trim() ? kind : null }).eq('id', dealId)),
    onSuccess: onDone,
  });
  return (
    <div className="space-y-3">
      <Field label="Next step">
        <Textarea autoFocus value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="e.g. Send LOI draft to landlord's broker" />
      </Field>
      <FieldGroup label="Type">
        <div className="flex flex-wrap gap-1">
          {[...ACTIVITY_TYPES, null].map((t) => (
            <button
              key={t ?? 'other'}
              onClick={() => setKind(t)}
              className={cx('rounded-md px-2.5 py-1 text-xs font-medium', kind === t ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-indigo-300')}
            >
              {t ? ACTIVITY[t].label : 'Other'}
            </button>
          ))}
        </div>
      </FieldGroup>
      <Field label="By (optional)">
        <Input type="date" value={by} onChange={(e) => setBy(e.target.value)} className="!w-44" />
      </Field>
      <p className="text-xs text-slate-500">Shows on the home page To do list and in the morning summary. Calls and meetings count toward your goals when checked off.</p>
      <ErrorBox error={save.error} />
      <div className="flex justify-end">
        <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
      </div>
    </div>
  );
}

function AddDealContact({ onAdd }: { onAdd: (contactId: string, role: string) => void }) {
  const [role, setRole] = useState('');
  return (
    <div className="space-y-3">
      <Field label="Role" hint="e.g. landlord's broker, decision maker, attorney">
        <Input value={role} onChange={(e) => setRole(e.target.value)} />
      </Field>
      <Field label="Person">
        <ContactPicker onPick={(o) => onAdd(o.id, role.trim() || 'other')} />
      </Field>
    </div>
  );
}

type Payment = NonNullable<ReturnType<typeof useDeal>['data']>['payments'][number];

function PaymentsCard({ dealId, payments, onChange }: { dealId: string; payments: Payment[]; onChange: () => void }) {
  const [editing, setEditing] = useState<Payment | null>(null);
  return (
    <Card title="Payments">
      {payments.length === 0 ? (
        <Empty>No payments yet. Enter the commission terms below and click “Save & recalculate”.</Empty>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-slate-500">
            <tr>
              <th className="pb-1 font-medium">Payment</th>
              <th className="pb-1 font-medium">Expected</th>
              <th className="pb-1 font-medium">Status</th>
              <th className="pb-1 text-right font-medium">Amount</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {payments.map((p) => (
              <tr key={p.id}>
                <td className="py-1.5 text-slate-700">{p.label}</td>
                <td className="py-1.5 text-slate-600">{p.expected_date ? date(p.expected_date) : '—'}</td>
                <td className="py-1.5">
                  <Pill color={PAYMENT_COLOR[p.effective_status ?? 'projected']}>{p.effective_status}</Pill>
                  {p.status === 'received' && p.received_date && <span className="ml-1 text-xs text-slate-500">{date(p.received_date)}</span>}
                  {p.status === 'invoiced' && p.invoiced_date && <span className="ml-1 text-xs text-slate-500">inv. {date(p.invoiced_date)}</span>}
                </td>
                <td className="py-1.5 text-right font-medium">{money(p.status === 'received' ? (p.received_amount ?? p.amount) : p.amount)}</td>
                <td className="py-1.5 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>
                    Update
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Modal title="Update payment" open={!!editing} onClose={() => setEditing(null)}>
        {editing && (
          <PaymentForm
            dealId={dealId}
            payment={editing}
            onDone={() => {
              setEditing(null);
              onChange();
            }}
          />
        )}
      </Modal>
    </Card>
  );
}

function PaymentForm({ dealId, payment, onDone }: { dealId: string; payment: Payment; onDone: () => void }) {
  const [status, setStatus] = useState(payment.status ?? 'projected');
  const [invoiced, setInvoiced] = useState(payment.invoiced_date ?? todayISO());
  const [received, setReceived] = useState(payment.received_date ?? todayISO());
  const [amount, setAmount] = useState(String(payment.received_amount ?? payment.amount ?? ''));
  const [notes, setNotes] = useState(payment.notes ?? '');
  const save = useMutation({
    mutationFn: async () => {
      unwrap(
        await supabase
          .from('payments')
          .update({
            status,
            invoiced_date: status === 'projected' ? null : status === 'invoiced' || payment.invoiced_date ? invoiced : null,
            received_date: status === 'received' ? received : null,
            received_amount: status === 'received' ? Number(amount) : null,
            notes: notes || null,
          })
          .eq('id', payment.id!),
      );
      // Chase reminders close and post-close touchpoints appear as payments come in.
      await syncDeal(dealId);
    },
    onSuccess: onDone,
  });
  return (
    <div className="space-y-3">
      <div className="text-sm text-slate-600">
        {payment.label} · {money(payment.amount)} · expected {date(payment.expected_date)}
      </div>
      <Field label="Status">
        <Select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="projected">Projected</option>
          <option value="invoiced">Invoiced</option>
          <option value="received">Received</option>
        </Select>
      </Field>
      {status === 'invoiced' && (
        <Field label="Invoiced on">
          <Input type="date" value={invoiced} onChange={(e) => setInvoiced(e.target.value)} />
        </Field>
      )}
      {status === 'received' && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Received on">
            <Input type="date" value={received} onChange={(e) => setReceived(e.target.value)} />
          </Field>
          <Field label="Amount received">
            <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
        </div>
      )}
      <Field label="Notes">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </Field>
      {status !== 'projected' && <p className="text-xs text-slate-500">Invoiced and received payments are never changed by recalculation.</p>}
      <ErrorBox error={save.error} />
      <div className="flex justify-end">
        <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
      </div>
    </div>
  );
}

function DealInfoForm({ deal, onDone }: { deal: NonNullable<ReturnType<typeof useDeal>['data']>['deal']; onDone: () => void }) {
  const [f, setF] = useState({
    name: deal.name,
    expected_close_date: deal.expected_close_date ?? '',
    next_step: deal.next_step ?? '',
    next_step_date: deal.next_step_date ?? '',
    notes: deal.notes ?? '',
  });
  const [org, setOrg] = useState(deal.organizations ? { id: deal.organizations.id, label: deal.organizations.name } : null);
  const [building, setBuilding] = useState(deal.buildings ? { id: deal.buildings.id, label: deal.buildings.name || deal.buildings.street || '' } : null);
  const [primary, setPrimary] = useState(deal.contacts ? { id: deal.contacts.id, label: personName(deal.contacts) } : null);
  const [picking, setPicking] = useState<'org' | 'building' | 'contact' | null>(null);
  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await supabase
          .from('deals')
          .update({
            name: f.name.trim(),
            expected_close_date: f.expected_close_date || null,
            next_step: f.next_step || null,
            next_step_date: f.next_step_date || null,
            notes: f.notes || null,
            org_id: org?.id ?? null,
            building_id: building?.id ?? null,
            suite_id: building?.id === deal.building_id ? deal.suite_id : null,
            primary_contact_id: primary?.id ?? null,
          })
          .eq('id', deal.id),
      ),
    onSuccess: onDone,
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const chip = (label: string | undefined, kind: 'org' | 'building' | 'contact', clear?: () => void) => (
    <div className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-sm">
      <span className="flex-1">{label ?? <span className="text-slate-400">none</span>}</span>
      <button className="text-xs text-indigo-600 hover:underline" onClick={() => setPicking(kind)}>
        change
      </button>
      {clear && label && (
        <button className="text-xs text-slate-500 hover:underline" onClick={clear}>
          clear
        </button>
      )}
    </div>
  );
  return (
    <div className="space-y-3">
      <Field label="Name">
        <Input value={f.name} onChange={set('name')} />
      </Field>
      <FieldGroup label="Primary contact">{picking === 'contact' ? <ContactPicker onPick={(o) => (setPrimary(o), setPicking(null))} /> : chip(primary?.label, 'contact')}</FieldGroup>
      <FieldGroup label="Company">{picking === 'org' ? <OrgPicker onPick={(o) => (setOrg(o), setPicking(null))} /> : chip(org?.label, 'org', () => setOrg(null))}</FieldGroup>
      <FieldGroup label="Building">{picking === 'building' ? <BuildingPicker onPick={(o) => (setBuilding(o), setPicking(null))} /> : chip(building?.label, 'building', () => setBuilding(null))}</FieldGroup>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Next step" className="col-span-2">
          <Input value={f.next_step} onChange={set('next_step')} />
        </Field>
        <Field label="Next step by">
          <Input type="date" value={f.next_step_date} onChange={set('next_step_date')} />
        </Field>
        <Field label="Expected close">
          <Input type="date" value={f.expected_close_date} onChange={set('expected_close_date')} />
        </Field>
      </div>
      <Field label="Notes">
        <Textarea value={f.notes} onChange={set('notes')} rows={4} />
      </Field>
      <ErrorBox error={save.error} />
      <div className="flex justify-end">
        <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()} disabled={!f.name.trim()}>
          Save
        </Button>
      </div>
    </div>
  );
}
